import type { PersonalizationField } from "@erc/shared";
import { Plus, Trash2 } from "lucide-react";

const TYPES: { v: PersonalizationField["type"]; label: string }[] = [
  { v: "text_input", label: "Metin" },
  { v: "dropdown", label: "Açılır liste" },
  { v: "unlabeled_upload", label: "Dosya" },
  { v: "labeled_upload", label: "Etiketli dosya" },
];

// Ham personalization alanlarini (etiket/tip/zorunlu/talimat/secenek/limit) duzenler.
// Analiz Detayi, Tasarim ve Mockup Studio tarafindan ortak kullanilir. Max 5 alan.
export function PersonalizationFieldsEditor({ fields, onChange }: {
  fields: PersonalizationField[];
  onChange: (f: PersonalizationField[]) => void;
}) {
  const update = (i: number, patch: Partial<PersonalizationField>) =>
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i));
  const add = () => {
    if (fields.length >= 5) return;
    onChange([...fields, { label: "", type: "text_input", required: false, maxChar: 256 }]);
  };

  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-gray-200 bg-white p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <input value={f.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Alan adı (ör. Name)"
              className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300" />
            <select value={f.type} onChange={(e) => update(i, { type: e.target.value as PersonalizationField["type"] })}
              className="rounded border border-gray-200 px-2 py-1 text-xs">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-[11px] text-gray-500">
              <input type="checkbox" checked={f.required} onChange={(e) => update(i, { required: e.target.checked })} /> Zorunlu
            </label>
            <button onClick={() => remove(i)} title="Sil" className="text-gray-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          {/* Cok satirli: Etsy talimati satir satir ("Ex:" / Line 1 / Line 2…) */}
          <textarea value={f.instructions || ""} onChange={(e) => update(i, { instructions: e.target.value })} placeholder="Talimat / örnek (opsiyonel)"
            aria-label="Talimat" rows={Math.min(8, Math.max(2, (f.instructions || "").split("\n").length))}
            className="w-full resize-y rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300" />
          {(f.type === "dropdown" || f.type === "labeled_upload") && (
            <input value={(f.options || []).join(", ")}
              onChange={(e) => update(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder={f.type === "dropdown" ? "Seçenekler (virgülle ayır)" : "Slot etiketleri (virgülle ayır)"}
              className="w-full rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300" />
          )}
          <div className="flex items-center gap-2">
            {f.type === "text_input" && (
              <label className="flex items-center gap-1 text-[11px] text-gray-500">Karakter
                <input type="number" min={1} max={1024} value={f.maxChar || 256}
                  onChange={(e) => update(i, { maxChar: Number(e.target.value) || 256 })}
                  className="w-20 rounded border border-gray-200 px-2 py-1 text-xs" />
              </label>
            )}
            {(f.type === "unlabeled_upload" || f.type === "labeled_upload") && (
              <label className="flex items-center gap-1 text-[11px] text-gray-500">Max dosya
                <input type="number" min={1} max={10} value={f.maxFiles || 1}
                  onChange={(e) => update(i, { maxFiles: Number(e.target.value) || 1 })}
                  className="w-20 rounded border border-gray-200 px-2 py-1 text-xs" />
              </label>
            )}
          </div>
        </div>
      ))}
      {fields.length < 5 && (
        <button onClick={add}
          className="flex items-center gap-1 rounded border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50">
          <Plus className="h-3.5 w-3.5" /> Alan Ekle
        </button>
      )}
    </div>
  );
}
